import React, { useState } from "react";
import { companyInfo } from "../data/mock";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import {
  Phone,
  Mail,
  MapPin,
  MessageCircle,
  Send,
  Clock,
} from "lucide-react";
import { toast } from "sonner";

export const ContactSection = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.message) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setSubmitting(true);
    // Mock submission
    setTimeout(() => {
      toast.success("Your enquiry has been submitted! We'll contact you shortly.");
      setForm({ name: "", email: "", phone: "", company: "", message: "" });
      setSubmitting(false);
    }, 1200);
  };

  const contactCards = [
    {
      icon: Phone,
      title: "Call Us",
      value: companyInfo.phone,
      href: `tel:${companyInfo.phone}`,
    },
    {
      icon: MessageCircle,
      title: "WhatsApp",
      value: companyInfo.phone,
      href: `https://wa.me/${companyInfo.whatsapp}`,
    },
    {
      icon: Mail,
      title: "Email",
      value: companyInfo.email,
      href: `mailto:${companyInfo.email}`,
    },
    {
      icon: Clock,
      title: "Working Hours",
      value: "Mon - Sat: 8AM - 8PM",
      href: null,
    },
  ];

  return (
    <section id="contact" className="py-24 md:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 bg-emerald-50 rounded-full px-4 py-1.5 mb-6">
            <div className="w-2 h-2 rounded-full bg-emerald-500" />
            <span className="text-emerald-700 text-sm font-semibold uppercase tracking-wider">
              Get In Touch
            </span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
            Contact AM Scrap Dealers
          </h2>
          <p className="text-slate-500 text-lg max-w-2xl mx-auto">
            Ready to sell your scrap? Get in touch with us for a quick quote and same-day pickup.
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-10">
          {/* Contact Info + Map */}
          <div className="lg:col-span-2 space-y-5">
            {contactCards.map((card, i) => {
              const Icon = card.icon;
              const Wrapper = card.href ? "a" : "div";
              return (
                <Wrapper
                  key={i}
                  {...(card.href
                    ? { href: card.href, target: "_blank", rel: "noopener noreferrer" }
                    : {})}
                  className="flex items-center gap-4 p-4 rounded-xl bg-stone-50 hover:bg-emerald-50 transition-colors duration-200 group cursor-pointer"
                >
                  <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center flex-shrink-0 group-hover:bg-emerald-200 transition-colors">
                    <Icon className="w-5 h-5 text-emerald-700" />
                  </div>
                  <div>
                    <div className="text-xs text-slate-400 font-medium uppercase tracking-wider">
                      {card.title}
                    </div>
                    <div className="text-slate-800 font-semibold text-sm">
                      {card.value}
                    </div>
                  </div>
                </Wrapper>
              );
            })}

            {/* Address */}
            <div className="flex items-start gap-4 p-4 rounded-xl bg-stone-50">
              <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center flex-shrink-0">
                <MapPin className="w-5 h-5 text-emerald-700" />
              </div>
              <div>
                <div className="text-xs text-slate-400 font-medium uppercase tracking-wider">
                  Our Address
                </div>
                <div className="text-slate-800 font-semibold text-sm">
                  {companyInfo.address}
                </div>
              </div>
            </div>

            {/* Map */}
            <div className="rounded-xl overflow-hidden border border-slate-200 h-48">
              <iframe
                src={companyInfo.mapEmbedUrl}
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="AM Scrap Dealers Location"
              />
            </div>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-3">
            <Card className="border-0 shadow-xl shadow-emerald-900/[0.06] rounded-2xl">
              <CardContent className="p-8 md:p-10">
                <h3 className="text-xl font-bold text-slate-900 mb-2">
                  Send Us an Enquiry
                </h3>
                <p className="text-slate-500 text-sm mb-8">
                  Fill the form below and we'll get back to you within 1 hour.
                </p>

                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="grid sm:grid-cols-2 gap-5">
                    <div className="space-y-2">
                      <Label htmlFor="name" className="text-slate-700 text-sm font-semibold">
                        Full Name *
                      </Label>
                      <Input
                        id="name"
                        name="name"
                        value={form.name}
                        onChange={handleChange}
                        placeholder="Your name"
                        className="rounded-xl border-slate-200 focus:border-emerald-500 focus:ring-emerald-500/20 h-11"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-slate-700 text-sm font-semibold">
                        Email
                      </Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={form.email}
                        onChange={handleChange}
                        placeholder="your@email.com"
                        className="rounded-xl border-slate-200 focus:border-emerald-500 focus:ring-emerald-500/20 h-11"
                      />
                    </div>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-5">
                    <div className="space-y-2">
                      <Label htmlFor="phone" className="text-slate-700 text-sm font-semibold">
                        Phone Number *
                      </Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={form.phone}
                        onChange={handleChange}
                        placeholder="+91 6374966893"
                        className="rounded-xl border-slate-200 focus:border-emerald-500 focus:ring-emerald-500/20 h-11"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="company" className="text-slate-700 text-sm font-semibold">
                        Company Name
                      </Label>
                      <Input
                        id="company"
                        name="company"
                        value={form.company}
                        onChange={handleChange}
                        placeholder="Your company"
                        className="rounded-xl border-slate-200 focus:border-emerald-500 focus:ring-emerald-500/20 h-11"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="message" className="text-slate-700 text-sm font-semibold">
                      Message *
                    </Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={form.message}
                      onChange={handleChange}
                      placeholder="Tell us about the scrap you want to sell..."
                      rows={4}
                      className="rounded-xl border-slate-200 focus:border-emerald-500 focus:ring-emerald-500/20 resize-none"
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={submitting}
                    className="w-full bg-emerald-700 hover:bg-emerald-600 text-white rounded-xl py-6 text-base font-semibold shadow-lg shadow-emerald-700/15 hover:shadow-emerald-600/25 transition-all duration-300 hover:-translate-y-0.5"
                  >
                    {submitting ? (
                      <span className="flex items-center gap-2">
                        <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Sending...
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        <Send className="w-4 h-4" />
                        Submit Enquiry
                      </span>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};
